import { useState, useCallback, useEffect } from "react";
import {
  adminLoginService,
  deleteUserAccount,
  getCurrentUser,
  updateUserProfile,
  getAllUsers,
} from "../services/userService";
import { User } from "../types/User";
import * as authStorage from "../lib/authStorage";

export const useUser = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const storedUser = authStorage.getUser();
    if (storedUser) {
      setUser(storedUser);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const handleAuthChange = () => {
      setUser(null);
    };

    window.addEventListener("auth-cleared", handleAuthChange);

    return () => {
      window.removeEventListener("auth-cleared", handleAuthChange);
    };
  }, []);

  const adminLogin = useCallback(
    async (username: string, password: string, rememberMe: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminLoginService(username, password);
        const accessToken = response.accessToken || response.token;

        if (accessToken) {
          const adminUser: User = {
            _id: "admin-placeholder-id",
            email: "admin@movingrolls.com",
            firstName: "Admin",
            lastName: "User",
            username: "admin",
            type: "admin",
            language: "en",
            isPremium: true,
            gender: "other",
            countryCode: "+91",
            phone: "0000000000",
            timezone: 5.5,
            birthDate: new Date().toISOString(),
            panCard: "**********",
            aadharCard: "************",
            city: "N/A",
            address: "N/A",
            isActivated: true,
            isVerified: true,
            location: "0,0",
            deviceId: "admin-device",
            platform: "android",
            deletedAt: null,
          };
          authStorage.saveAuthData(response, adminUser, rememberMe);
          setUser(adminUser);
        } else {
          throw new Error(
            response.resultMessage?.en || "Login failed: No token received",
          );
        }
        return response;
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.resultMessage?.en ||
          err.message ||
          "Invalid credentials";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const logout = useCallback(() => {
    authStorage.clearAuth();
  }, []);

  const deleteAccount = useCallback(
    async (userId: string) => {
      setLoading(true);
      setError(null);
      try {
        await deleteUserAccount(userId);
        logout();
      } catch (err) {
        setError("Failed to delete account");
      } finally {
        setLoading(false);
      }
    },
    [logout],
  );

  const getUser = useCallback(async (userId: string): Promise<User | null> => {
    setLoading(true);
    setError(null);
    try {
      const userData = await getCurrentUser(userId);
      setUser(userData);
      authStorage.saveAuthData(
        {
          accessToken: authStorage.getAccessToken()!,
          refreshToken: authStorage.getRefreshToken() || undefined,
        },
        userData,
        !!localStorage.getItem("accessToken"),
      );
      return userData;
    } catch (err) {
      setError("Failed to fetch user info");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProfile = useCallback(
    async (userId: string, userData: FormData) => {
      setLoading(true);
      setError(null);
      try {
        const updatedUser = await updateUserProfile(userId, userData);
        setUser(updatedUser);
        authStorage.saveAuthData(
          {
            accessToken: authStorage.getAccessToken()!,
            refreshToken: authStorage.getRefreshToken() || undefined,
          },
          updatedUser,
          !!localStorage.getItem("accessToken"),
        );
        return updatedUser;
      } catch (err) {
        setError("Failed to update profile");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getUsers = useCallback(async (): Promise<User[]> => {
    setLoading(true);
    setError(null);
    try {
      return await getAllUsers();
    } catch (err) {
      setError("Failed to fetch users");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    user,
    loading,
    error,
    adminLogin,
    logout,
    deleteAccount,
    getUser,
    updateProfile,
    getUsers,
  };
};
